import { Box, Icon, Typography, useTheme } from "@outschool/backpack";
import { faBolt } from "@outschool/icons";
import * as Time from "@outschool/time";
import { useSession } from "@outschool/ui-auth";
import React from "react";

import useSectionString from "../../../shared/hooks/useSectionString";

type NextStartTimeProps = {
  nextSectionStartTime?: string;
  nextOngoingMeetingStartTime?: string;
};

export function NextStartTime({
  nextSectionStartTime,
  nextOngoingMeetingStartTime,
}: NextStartTimeProps) {
  const { userTimeZone } = useSession();
  const { formatNextSessionStartTime } = useSectionString();
  const theme = useTheme();
  const [isToday, setIsToday] = React.useState(false);
  const [nextStartTimeString, setNextStartTimeString] = React.useState<
    string | null
  >(null);

  React.useEffect(() => {
    if (!nextSectionStartTime && !nextOngoingMeetingStartTime) {
      return;
    }

    // If nextSection is provided & not past, use that - else nextOngoingMeetingStartTime
    const nextDate =
      nextSectionStartTime &&
      Time.isDateInThePast(nextSectionStartTime, userTimeZone || "UTC")
        ? nextOngoingMeetingStartTime
        : nextSectionStartTime || nextOngoingMeetingStartTime;

    nextDate &&
      setNextStartTimeString(
        formatNextSessionStartTime(nextDate, userTimeZone || "UTC"),
      );
    nextDate && setIsToday(Time.isToday(nextDate, userTimeZone || "UTC"));
  }, [
    nextSectionStartTime,
    nextOngoingMeetingStartTime,
    formatNextSessionStartTime,
    userTimeZone,
  ]);

  if (!nextStartTimeString) {
    return null;
  }

  return (
    <Box
      flex
      sx={{
        alignItems: "center",
        [theme.breakpoints.down("md")]: {
          gap: "0.4em",
        },
      }}
    >
      {isToday && (
        <Box
          flex
          sx={{
            [theme.breakpoints.up("md")]: {
              width: 48,
              justifyContent: "center",
            },
          }}
        >
          <LightningIcon />
        </Box>
      )}
      {!isToday && (
        <Box
          sx={{
            [theme.breakpoints.up("md")]: {
              width: 48,
            },
            [theme.breakpoints.down("md")]: {
              width: 20,
            },
          }}
        />
      )}
      <Typography
        variant="body1"
        sx={{
          color: isToday ? "info.600" : "black",
          fontWeight: 500,
          [theme.breakpoints.down("md")]: {
            fontSize: 14,
          },
        }}
      >
        {nextStartTimeString}
      </Typography>
    </Box>
  );
}

const LightningIcon = () => {
  const theme = useTheme();

  return (
    <Icon
      icon={faBolt}
      sx={{
        color: "info.500",
        [theme.breakpoints.up("md")]: {
          height: 24,
          width: 24,
        },
        [theme.breakpoints.down("md")]: {
          height: 20,
          width: 20,
        },
      }}
    />
  );
};
